const { Room, RoomEvent, ConnectionState } = require('livekit-client');
const { LK_EVENTS } = require('./lk-events');

// Krisp loaded dynamically from window.LKKrisp (separate bundle)
function _getKrisp() {
  const k = typeof window !== 'undefined' && window.LKKrisp;
  if (!k) throw new Error('Krisp not loaded. Add <script src="/livekit/dist/krisp-noise-filter.min.js"></script> before use.');
  return k;
}

const DEFAULT_OPTIONS = {
  url: 'ws://localhost:7880',
  tokenEndpoint: '/api/livekit/token',
  roomName: '',
  user: { userId: '', userName: '', role: 'participant' },
  authToken: '',
  roomOptions: {
    adaptiveStream: true,
    dynacast: true,
    videoCaptureDefaults: { resolution: { width: 640, height: 480, frameRate: 24 } },
    audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true }
  },
  autoConnect: false
};

class LKBase {
  /**
   * @param {HTMLElement|null} container - DOM element to render into (null for headless)
   * @param {object} options - Merge with DEFAULT_OPTIONS
   */
  constructor(container, options) {
    this.container = container;
    this.options = Object.assign({}, DEFAULT_OPTIONS, options, {
      roomOptions: Object.assign({}, DEFAULT_OPTIONS.roomOptions, options.roomOptions || {})
    });
    this.room = null;
    this._listeners = {};
    this._connected = false;
    this._destroyed = false;
    this._krispFilter = null;
    this._noiseFilterEnabled = false;

    if (this.options.autoConnect) {
      // Defer to allow subclass constructor to finish
      Promise.resolve().then(() => this.connect());
    }
  }

  // ─── Lifecycle ────────────────────────────────────────────────────

  async connect() {
    if (this._destroyed) throw new Error('Instance has been destroyed');
    if (this._connected) return;

    try {
      console.log('[LKBase] Fetching token for room:', this.options.roomName);
      const token = await this._fetchToken();
      console.log('[LKBase] Token received, connecting to:', this.options.url);
      this.room = new Room(this.options.roomOptions);
      this._setupRoomListeners();

      await this.room.connect(this.options.url, token);
      console.log('[LKBase] Connected! Local identity:', this.room.localParticipant.identity);
      this._connected = true;

      // Unlock audio playback (browser autoplay policy)
      this.room.startAudio().catch(() => {
        console.warn('[LKBase] startAudio blocked, will retry on user click');
        const unlock = () => {
          this.room && this.room.startAudio();
          document.removeEventListener('click', unlock);
        };
        document.addEventListener('click', unlock, { once: true });
      });

      this._renderUI();
      this._emit(LK_EVENTS.CONNECTED, {
        room: this.room,
        localParticipant: this.room.localParticipant
      });
    } catch (err) {
      console.error('[LKBase] Connect failed:', err);
      this._emit(LK_EVENTS.ERROR, { error: err, context: 'connect' });
      throw err;
    }
  }

  async disconnect() {
    if (!this._connected || !this.room) return;

    this._cleanupUI();
    this._teardownRoomListeners();
    await this.room.disconnect();
    this.room = null;
    this._connected = false;

    this._emit(LK_EVENTS.DISCONNECTED, { reason: 'manual' });
  }

  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;

    if (this._connected) {
      this._cleanupUI();
      this._teardownRoomListeners();
      this._krispFilter = null;
      this._noiseFilterEnabled = false;
      if (this.room) {
        this.room.disconnect();
        this.room = null;
      }
      this._connected = false;
    }

    this._listeners = {};
    if (this.container) {
      this.container.innerHTML = '';
    }
  }

  // ─── State queries ────────────────────────────────────────────────

  get isConnected() {
    return this._connected && this.room &&
      this.room.state === ConnectionState.Connected;
  }

  get localParticipant() {
    return this.room ? this.room.localParticipant : null;
  }

  getParticipants() {
    return this.room ? this.room.remoteParticipants : new Map();
  }

  getParticipantCount() {
    if (!this.room) return 0;
    return this.room.remoteParticipants.size + 1; // +1 for local
  }

  // ─── Noise filter (Krisp) ────────────────────────────────────────

  /**
   * Enable Krisp noise cancellation on the local microphone track.
   * Works on any module that publishes audio (Camera, Audio, Screen).
   */
  async enableNoiseFilter() {
    if (!this.room) throw new Error('Not connected');
    if (this._noiseFilterEnabled) return;

    const { isKrispNoiseFilterSupported, KrispNoiseFilter } = _getKrisp();
    if (!isKrispNoiseFilterSupported()) {
      console.warn('[LKBase] Krisp noise filter not supported in this browser');
      this._emit(LK_EVENTS.ERROR, { error: new Error('Noise filter not supported'), context: 'noiseFilter' });
      return;
    }

    try {
      this._krispFilter = KrispNoiseFilter();
      console.log('[LKBase] Krisp filter created, setting processor...');

      const micPub = this.room.localParticipant.getTrackPublication('microphone');
      if (micPub && micPub.track) {
        await micPub.track.setProcessor(this._krispFilter);
        this._noiseFilterEnabled = true;
        console.log('[LKBase] Krisp noise filter enabled');
      } else {
        console.warn('[LKBase] No microphone track to apply noise filter');
      }
    } catch (err) {
      console.error('[LKBase] Failed to enable noise filter:', err);
      this._emit(LK_EVENTS.ERROR, { error: err, context: 'noiseFilter' });
    }
  }

  /**
   * Disable Krisp noise cancellation.
   */
  async disableNoiseFilter() {
    if (!this._noiseFilterEnabled || !this.room) return;

    try {
      const micPub = this.room.localParticipant.getTrackPublication('microphone');
      if (micPub && micPub.track) {
        await micPub.track.stopProcessor();
      }
    } catch (e) {
      // Ignore if no processor active
    }

    this._krispFilter = null;
    this._noiseFilterEnabled = false;
    console.log('[LKBase] Krisp noise filter disabled');
  }

  /**
   * Toggle noise filter on/off.
   * @returns {boolean} New state
   */
  async toggleNoiseFilter() {
    if (this._noiseFilterEnabled) {
      await this.disableNoiseFilter();
    } else {
      await this.enableNoiseFilter();
    }
    return this._noiseFilterEnabled;
  }

  /** @returns {boolean} */
  get isNoiseFilterEnabled() {
    return this._noiseFilterEnabled;
  }

  /** @returns {boolean} */
  static get isNoiseFilterSupported() {
    try {
      const { isKrispNoiseFilterSupported } = _getKrisp();
      return isKrispNoiseFilterSupported();
    } catch (e) {
      return false;
    }
  }

  // ─── Event system ────────────────────────────────────────────────

  on(event, callback) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(callback);
    return this;
  }

  off(event, callback) {
    const list = this._listeners[event];
    if (!list) return this;
    const idx = list.indexOf(callback);
    if (idx !== -1) list.splice(idx, 1);
    return this;
  }

  once(event, callback) {
    const wrapper = (data) => {
      this.off(event, wrapper);
      callback(data);
    };
    return this.on(event, wrapper);
  }

  _emit(event, data) {
    const list = this._listeners[event];
    if (!list) return;
    for (const cb of list) {
      try { cb(data); } catch (e) { console.error(`[LK] Event handler error (${event}):`, e); }
    }
  }

  // ─── Protected hooks (override in subclasses) ────────────────────

  _onParticipantConnected(participant) {}
  _onParticipantDisconnected(participant) {}
  _onTrackSubscribed(track, publication, participant) {}
  _onTrackUnsubscribed(track, publication, participant) {}
  _onLocalTrackPublished(publication, participant) {}
  _onActiveSpeakersChanged(speakers) {}

  /** Build DOM inside this.container. Override in subclasses. */
  _renderUI() {}

  /** Tear down DOM. Override in subclasses. */
  _cleanupUI() {}

  // ─── Internal ────────────────────────────────────────────────────

  async _fetchToken() {
    const resp = await fetch(this.options.tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.options.authToken
          ? { 'Authorization': 'Bearer ' + this.options.authToken }
          : {})
      },
      body: JSON.stringify({
        roomName: this.options.roomName,
        role: this.options.user.role
      })
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to fetch token');
    }

    const data = await resp.json();
    return data.token;
  }

  _setupRoomListeners() {
    if (!this.room) return;
    const r = this.room;

    r.on(RoomEvent.ParticipantConnected, (p) => {
      this._onParticipantConnected(p);
      this._emit(LK_EVENTS.PARTICIPANT_JOINED, { participant: p });
    });

    r.on(RoomEvent.ParticipantDisconnected, (p) => {
      this._onParticipantDisconnected(p);
      this._emit(LK_EVENTS.PARTICIPANT_LEFT, { participant: p });
    });

    r.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
      this._onTrackSubscribed(track, pub, participant);
      this._emit(LK_EVENTS.TRACK_SUBSCRIBED, { track, publication: pub, participant });
    });

    r.on(RoomEvent.TrackUnsubscribed, (track, pub, participant) => {
      this._onTrackUnsubscribed(track, pub, participant);
      this._emit(LK_EVENTS.TRACK_UNSUBSCRIBED, { track, publication: pub, participant });
    });

    r.on(RoomEvent.TrackMuted, (pub, participant) => {
      this._emit(LK_EVENTS.TRACK_MUTED, { publication: pub, participant });
    });

    r.on(RoomEvent.TrackUnmuted, (pub, participant) => {
      this._emit(LK_EVENTS.TRACK_UNMUTED, { publication: pub, participant });
    });

    r.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      this._onActiveSpeakersChanged(speakers);
      for (const s of speakers) {
        this._emit(LK_EVENTS.SPEAKING_CHANGED, { participant: s, isSpeaking: true });
      }
    });

    r.on(RoomEvent.LocalTrackPublished, (pub, participant) => {
      console.log('[LKBase] LocalTrackPublished:', pub.kind, pub.source);
      this._onLocalTrackPublished(pub, participant);
      this._emit(LK_EVENTS.LOCAL_TRACK_PUBLISHED, { publication: pub, participant });
    });

    r.on(RoomEvent.LocalTrackUnpublished, (pub, participant) => {
      this._emit(LK_EVENTS.LOCAL_TRACK_UNPUBLISHED, { publication: pub, participant });
    });

    r.on(RoomEvent.Reconnecting, () => {
      this._emit(LK_EVENTS.RECONNECTING, {});
    });

    r.on(RoomEvent.Reconnected, () => {
      this._emit(LK_EVENTS.RECONNECTED, {});
    });

    r.on(RoomEvent.Disconnected, (reason) => {
      this._connected = false;
      this._cleanupUI();
      this._emit(LK_EVENTS.DISCONNECTED, { reason });
    });

    r.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
      this._emit(LK_EVENTS.DATA_RECEIVED, { payload, participant, kind, topic });
    });
  }

  _teardownRoomListeners() {
    if (this.room) {
      this.room.removeAllListeners();
    }
  }
}

module.exports = { LKBase };
